/**
 * Deteccion automatica de manhwa / webtoon.
 *
 * Los manhwa se publican como una tira vertical continua y se leen
 * desplazando hacia abajo, no pasando paginas. Ni los generos ni la fuente
 * sirven para distinguirlos: Asura y MangaDex no devuelven generos, y los
 * de Zonatmo son tematicos ("Accion", "Romance"), sin etiqueta de formato.
 *
 * Lo que si distingue es la forma de la imagen. Medido sobre fuentes reales:
 *
 *     manhwa   760x14699 (r=19.3)   800x5530 (r=6.9)   720x10930 (r=15.2)
 *     manga    873x1240  (r=1.4)    961x1400 (r=1.5)
 *
 * Entre 1.5 y 6.3 hay un hueco enorme, asi que el umbral es comodo.
 */

import { cabeceraImagen, descargarImagen } from './imagenes'

export type Modo = 'paginado' | 'webtoon'

const UMBRAL = 2.5
/**
 * Ninguna pagina de manga llega a esta proporcion: las vinetas partidas mas
 * alargadas que se han visto (Berserk, 379x1400) se quedan en 3.7. Asi que
 * una sola pagina asi ya basta para decidir, sin depender de la mediana.
 */
const UMBRAL_SEGURO = 4.5
const PAGINAS_A_MEDIR = 3
const PREFIJO = 'yomou.formato.'
/** Con esto sobra para la cabecera de cualquier formato que devuelve Suwayomi. */
const BYTES_CABECERA = 65536

/**
 * Sube cuando cambia la forma de medir. Sirve para volver a mirar una vez las
 * series decididas con una version anterior: mientras la medicion estuvo rota
 * los manhwa salian como manga, y quien lo arreglara a mano se quedaria con
 * ese apaño para siempre.
 */
const VERSION_DETECCION = 2

interface FormatoGuardado {
  modo: Modo
  /** true si lo eligio el usuario a mano; entonces no se vuelve a detectar. */
  manual: boolean
  /** Version de la deteccion que lo decidio. Las viejas no la traen. */
  version?: number
}

export function leerFormato(mangaId: number): FormatoGuardado | null {
  try {
    const crudo = localStorage.getItem(PREFIJO + mangaId)
    return crudo ? (JSON.parse(crudo) as FormatoGuardado) : null
  } catch {
    return null
  }
}

export function guardarFormato(mangaId: number, modo: Modo, manual: boolean) {
  localStorage.setItem(
    PREFIJO + mangaId,
    JSON.stringify({ modo, manual, version: VERSION_DETECCION }),
  )
}

/** Si conviene volver a medir aunque ya haya algo guardado. */
export function hayQueMedir(guardado: FormatoGuardado | null): boolean {
  if (!guardado) return true
  if (!guardado.manual) return true
  return guardado.version !== VERSION_DETECCION
}

interface Medida {
  ancho: number
  alto: number
}

/**
 * Saca el tamano de la cabecera del fichero, sin descodificar la imagen.
 *
 * Antes esto se hacia con `createImageBitmap`, y fallaba justo en las paginas
 * que importan: Safari en iOS se niega a descodificar imagenes muy grandes, y
 * una tira de manhwa de 800x14699 pasa de largo su limite. Se median bien
 * solo las paginas que NO son tira —el banner apaisado con el que abren casi
 * todos los manhwa—, asi que la mediana salia baja y la serie acababa
 * detectada como manga. Es lo que pasaba con Caballero en Eterna Regresion:
 * pagina 0 de 1920x1286 y el resto tiras de 720x5498.
 *
 * La cabecera son los primeros bytes y siempre se puede leer, mida lo que
 * mida la imagen.
 */
function medirCabecera(vista: DataView): Medida | null {
  const bytes = vista.byteLength
  const texto = (inicio: number, largo: number) => {
    let salida = ''
    for (let i = 0; i < largo; i++) salida += String.fromCharCode(vista.getUint8(inicio + i))
    return salida
  }

  // WebP, que es lo que devuelve Suwayomi
  if (bytes > 30 && texto(0, 4) === 'RIFF' && texto(8, 4) === 'WEBP') {
    const tipo = texto(12, 4)
    if (tipo === 'VP8 ') {
      return {
        ancho: vista.getUint16(26, true) & 0x3fff,
        alto: vista.getUint16(28, true) & 0x3fff,
      }
    }
    if (tipo === 'VP8L') {
      const bits = vista.getUint32(21, true)
      return { ancho: (bits & 0x3fff) + 1, alto: ((bits >> 14) & 0x3fff) + 1 }
    }
    if (tipo === 'VP8X') {
      const leer24 = (i: number) =>
        vista.getUint8(i) | (vista.getUint8(i + 1) << 8) | (vista.getUint8(i + 2) << 16)
      return { ancho: leer24(24) + 1, alto: leer24(27) + 1 }
    }
    return null
  }

  if (bytes > 24 && vista.getUint32(0) === 0x89504e47) {
    return { ancho: vista.getUint32(16), alto: vista.getUint32(20) }
  }

  if (bytes > 10 && texto(0, 3) === 'GIF') {
    return { ancho: vista.getUint16(6, true), alto: vista.getUint16(8, true) }
  }

  if (bytes > 4 && vista.getUint16(0) === 0xffd8) {
    let i = 2
    while (i + 9 < bytes) {
      if (vista.getUint8(i) !== 0xff) {
        i++
        continue
      }
      const marca = vista.getUint8(i + 1)
      // Los SOF llevan el tamano; se saltan los tres que no son de imagen
      const esSOF =
        (marca >= 0xc0 && marca <= 0xcf) && marca !== 0xc4 && marca !== 0xc8 && marca !== 0xcc
      if (esSOF) {
        return { alto: vista.getUint16(i + 5), ancho: vista.getUint16(i + 7) }
      }
      i += 2 + vista.getUint16(i + 2)
    }
  }

  return null
}

async function ratio(ruta: string): Promise<number | null> {
  try {
    const trozo = await cabeceraImagen(ruta, BYTES_CABECERA)
    const medida = trozo ? medirCabecera(trozo) : null
    if (medida && medida.ancho > 0 && medida.alto > 0) {
      return medida.alto / medida.ancho
    }
  } catch {
    // Formato raro o cabecera cortada: se intenta por las malas
  }

  // Ultimo recurso: descodificar. Baja la imagen entera, asi que solo se
  // llega aqui con formatos que medirCabecera no sabe leer.
  try {
    const blob = await descargarImagen(ruta)
    if (!blob) return null
    const bitmap = await createImageBitmap(blob)
    const valor = bitmap.height / bitmap.width
    bitmap.close()
    return Number.isFinite(valor) ? valor : null
  } catch {
    return null
  }
}

/**
 * Mide varias paginas y decide el modo por la mediana.
 *
 * Con una sola pagina no basta: en Berserk hay vinetas partidas de 379x1400
 * (r=3.7) que por si solas pareceria una tira vertical.
 */
export async function detectarModo(paginas: string[]): Promise<Modo | null> {
  const muestra = paginas.slice(0, PAGINAS_A_MEDIR)
  if (muestra.length === 0) return null

  // En paralelo: son tres cabeceras de nada, y en serie retrasaban el modo
  // de lectura lo que tardara la fuente mas lenta multiplicado por tres.
  const medidas = await Promise.all(muestra.map(ratio))
  const ratios = medidas.filter((valor): valor is number => valor !== null)
  if (ratios.length === 0) return null

  // Una sola pagina desmesuradamente alta ya lo dice todo, y evita depender
  // de la mediana cuando alguna medicion se pierde: casi todos los manhwa
  // abren con un banner apaisado, asi que con dos de tres paginas medidas la
  // mediana podria caer del lado equivocado.
  if (ratios.some((r) => r >= UMBRAL_SEGURO)) return 'webtoon'

  ratios.sort((a, b) => a - b)
  const mediana = ratios[Math.floor(ratios.length / 2)]!
  return mediana > UMBRAL ? 'webtoon' : 'paginado'
}
